// Shared resolution of an AI suggestion against a select-backed cell's options
// (e.g. Underlying Security, whose option ids are sibling securities'
// security_ref UUIDs and whose labels are their names). The AI metadata usually
// carries the option NAME, sometimes an id; the grid stores the ID and its
// validation NULLS any value that is not a listed option id (conditions.tsx).
// ONE matcher for every consumer - rest preview, accept write, store patch:
// match by id, then by exact name, then by case-insensitive trimmed name.

export interface AiSelectOption {
  id?: unknown;
  name?: unknown;
}

export interface ResolvedAiOption {
  id: string | number;
  label: string;
}

export const matchAiOption = (
  options: readonly AiSelectOption[] | null | undefined,
  aiValue: unknown
): ResolvedAiOption | null => {
  if (!options?.length || aiValue === null || aiValue === undefined || aiValue === '') {
    return null;
  }
  const wanted = String(aiValue);
  const relaxed = wanted.trim().toLowerCase();
  const match =
    options.find(option => String(option?.id) === wanted || String(option?.name) === wanted)
    ?? options.find(option => String(option?.name).trim().toLowerCase() === relaxed);
  if (match?.id === null || match?.id === undefined) {
    return null;
  }
  return { id: match.id as string | number, label: String(match.name ?? match.id) };
};

interface OptionsCell {
  options?: readonly AiSelectOption[] | null;
  aiFieldMetadata?: { value?: unknown } | null;
}

// Cell-level view of the matcher: resolve the cell's own pending AI suggestion
// (cell.aiFieldMetadata.value unless an explicit value is passed) against the
// cell's options.
export const resolveAiOptionValue = (
  cell: OptionsCell | null | undefined,
  aiValue: unknown = cell?.aiFieldMetadata?.value
): ResolvedAiOption | null => matchAiOption(cell?.options, aiValue);

// Accept-time write value for a grid cell: a non-select cell passes the raw AI
// value through; a select cell (options ARRAY present, even empty) must resolve
// to an option id - committing the raw name would just be nulled by the grid
// validation and the accepted value silently lost, so an unresolved suggestion
// is a hard accept error the caller must surface WITHOUT settling the finding.
export const resolveAcceptValue = (cell: OptionsCell): { value?: unknown; error?: string } => {
  if (!Array.isArray(cell.options)) {
    return { value: cell.aiFieldMetadata?.value };
  }
  const resolved = resolveAiOptionValue(cell);
  return resolved
    ? { value: resolved.id }
    : { error: `Cannot accept "${String(cell.aiFieldMetadata?.value ?? '')}": no matching security on this cap table` };
};
